import * as React from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { SVGs } from "../assets";
import { useSnackBar } from "../stores";

type TextLevel = 1 | 2 | 3 | 4 | 5 | "p" | "b" | "a";

export type TextProps<TAs extends React.ElementType = "p"> = {
  as?: TAs;
  level?: TextLevel;
  className?: string;
  children?: React.ReactNode;
  copyable?: boolean | string;
} & Omit<
  React.ComponentPropsWithoutRef<TAs>,
  "as" | "children" | "className" | "copyable"
>;

const levelClass: Record<TextLevel, string> = {
  1: "font-[700] text-[24px] leading-[1.0] tracking-[0px] align-middle",
  2: "font-semibold font-[600] text-[20px] leading-[28px] tracking-[0]",
  3: "font-[600] text-[18px] leading-[26px] tracking-[0]",
  4: "font-[500] text-[16px] leading-[1.0] tracking-[-0.03em]",
  5: "text-[12px] leading-[16px] tracking-[0]",
  p: "text-[14px] leading-[22px] tracking-[0]",
  b: "text-sm md:text-base font-semibold text-text-primary",
  a: "text-[14px] leading-[22px] tracking-[0] font-[600] underline hover:opacity-90",
};

function Text<TAs extends React.ElementType = "p">({
  as,
  level = "p",
  className,
  children,
  copyable,
  ...rest
}: TextProps<TAs>) {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackBar();
  const Comp = (as ?? (level === "a" ? "a" : "p")) as React.ElementType;

  const handleCopy = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const value = typeof copyable === "string" ? copyable : String(children);
      if (!value) return;
      void navigator.clipboard.writeText(value).then(() => {
        showSnackbar(t("general.copied"));
      });
    },
    [children, copyable, showSnackbar, t],
  );

  const content = (
    <Comp
      className={twMerge("font-sans font-normal", levelClass[level], className)}
      {...rest}
    >
      {children}
    </Comp>
  );

  if (!copyable) return content;
  return (
    <span className="inline-flex items-center gap-[4px]">
      {content}
      <button
        type="button"
        onClick={handleCopy}
        aria-label={t("general.copy")}
        className="inline-flex shrink-0 items-center hover:opacity-80"
      >
        <SVGs.Clipboard
          className={twMerge("h-[12px] w-[12px]", className)}
          aria-hidden
        />
      </button>
    </span>
  );
}

export default Text;
